import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useCreateGuest, useFriends, useGroupMembers, useGuests, useMe } from '../../api/hooks'
import { MeepleAvatar, ScorePad, ScorePadRow } from '../../primitives'
import { type DraftParticipant, useRecordPlayDraft } from '../../stores/recordPlayDraft'

type PickerEntry =
  | {
      kind: 'user'
      userId: string
      handle: string
      displayName: string
      avatarHue: number
      avatarShape: 'meeple' | 'circle'
    }
  | {
      kind: 'guest'
      guestId: string
      displayName: string
    }

const entryKey = (e: PickerEntry) => (e.kind === 'user' ? `u:${e.userId}` : `g:${e.guestId}`)

export const RecordPlayersStep = () => {
  const navigate = useNavigate()
  const me = useMe()
  const groupId = useRecordPlayDraft((s) => s.groupId)
  const groupMembers = useGroupMembers(groupId ?? '')
  const friends = useFriends()
  const guests = useGuests()
  const createGuest = useCreateGuest()
  const participants = useRecordPlayDraft((s) => s.participants)
  const setParticipants = useRecordPlayDraft((s) => s.setParticipants)

  const [addingGuest, setAddingGuest] = useState(false)
  const [guestName, setGuestName] = useState('')

  const userEntries: PickerEntry[] = groupId
    ? (groupMembers.data ?? [])
        .filter((m) => !m.blockedYou)
        .map((m) => ({
          kind: 'user',
          userId: m.userId,
          handle: m.handle,
          displayName: m.displayName,
          avatarHue: m.avatarHue,
          avatarShape: m.avatarShape,
        }))
    : [
        ...(me.data
          ? [
              {
                kind: 'user' as const,
                userId: me.data.id,
                handle: me.data.handle,
                displayName: me.data.displayName,
                avatarHue: me.data.avatarHue,
                avatarShape: me.data.avatarShape,
              },
            ]
          : []),
        ...(friends.data?.accepted ?? []).map((f) => ({
          kind: 'user' as const,
          userId: f.user.id,
          handle: f.user.handle,
          displayName: f.user.displayName,
          avatarHue: f.user.avatarHue,
          avatarShape: f.user.avatarShape,
        })),
      ]

  const guestEntries: PickerEntry[] = (guests.data ?? [])
    .filter((g) => (groupId ? g.groupId === groupId : g.groupId === null))
    .map((g) => ({ kind: 'guest', guestId: g.id, displayName: g.name }))

  const isUserPicked = (userId: string) => participants.some((p) => p.userId === userId)
  const isGuestPicked = (guestId: string) => participants.some((p) => p.guestId === guestId)
  const isPicked = (e: PickerEntry) =>
    e.kind === 'user' ? isUserPicked(e.userId) : isGuestPicked(e.guestId)

  const toggle = (e: PickerEntry) => {
    const already = isPicked(e)
    let next: DraftParticipant[]
    if (already) {
      next =
        e.kind === 'user'
          ? participants.filter((p) => p.userId !== e.userId)
          : participants.filter((p) => p.guestId !== e.guestId)
    } else {
      const draft: DraftParticipant =
        e.kind === 'user'
          ? {
              userId: e.userId,
              guestId: null,
              displayName: e.displayName,
              avatarHue: e.avatarHue,
              rank: participants.length + 1,
              tiedWithPrevious: false,
              score: null,
              subscores: {},
              outcomeTags: [],
            }
          : {
              userId: null,
              guestId: e.guestId,
              displayName: e.displayName,
              avatarHue: 0,
              rank: participants.length + 1,
              tiedWithPrevious: false,
              score: null,
              subscores: {},
              outcomeTags: [],
            }
      next = [...participants, draft]
    }
    // The store re-derives ranks from the finishing order.
    setParticipants(next)
  }

  const submitNewGuest = async () => {
    const name = guestName.trim()
    if (!name) return
    const guest = await createGuest.mutateAsync({ name, groupId: groupId ?? null })
    const draft: DraftParticipant = {
      userId: null,
      guestId: guest.id,
      displayName: guest.name,
      avatarHue: 0,
      rank: participants.length + 1,
      tiedWithPrevious: false,
      score: null,
      subscores: {},
      outcomeTags: [],
    }
    setParticipants([...participants, draft])
    setGuestName('')
    setAddingGuest(false)
  }

  const cancelAddGuest = () => {
    setGuestName('')
    setAddingGuest(false)
  }

  const canContinue = participants.length >= 2
  const goResults = () => {
    if (groupId) navigate({ to: '/group/$id/record/results', params: { id: groupId } })
    else navigate({ to: '/record/results' })
  }

  const renderRow = (e: PickerEntry) => {
    const picked = isPicked(e)
    const isYou = e.kind === 'user' && me.data?.id === e.userId
    return (
      <ScorePadRow
        key={entryKey(e)}
        onClick={() => toggle(e)}
        style={picked ? { background: 'var(--terra-bg)' } : undefined}
      >
        {e.kind === 'user' ? (
          <MeepleAvatar name={e.displayName} hue={e.avatarHue} shape={e.avatarShape} size={36} />
        ) : (
          <MeepleAvatar name={e.displayName} hue={0} shape="meeple" size={36} ghost />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {e.displayName}
            {isYou && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                you
              </span>
            )}
            {e.kind === 'guest' && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                guest
              </span>
            )}
          </div>
          {e.kind === 'user' && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>@{e.handle}</div>
          )}
        </div>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            border: `1.5px solid ${picked ? 'var(--terra)' : 'var(--line)'}`,
            background: picked ? 'var(--terra)' : 'transparent',
            color: '#fff6e8',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {picked ? '✓' : ''}
        </div>
      </ScorePadRow>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!groupId && userEntries.length <= 1 && guestEntries.length === 0 && (
        <div className="felt-card" style={{ padding: 12, color: 'var(--ink-2)' }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 16 }}>
            You need friends or guests to record a no-group play.
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Add friends on the Friends screen, or add a guest below.
          </div>
        </div>
      )}
      <ScorePad>
        {userEntries.map(renderRow)}
        {guestEntries.map(renderRow)}
        {addingGuest ? (
          <ScorePadRow style={{ background: 'var(--paper-soft, transparent)' }}>
            <MeepleAvatar name={guestName || '?'} hue={0} shape="meeple" size={36} ghost />
            <input
              type="text"
              autoFocus
              value={guestName}
              onChange={(ev) => setGuestName(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  ev.preventDefault()
                  submitNewGuest()
                } else if (ev.key === 'Escape') {
                  ev.preventDefault()
                  cancelAddGuest()
                }
              }}
              placeholder="Guest name"
              maxLength={40}
              style={{
                flex: 1,
                fontSize: 14,
                padding: '6px 8px',
                border: '1px solid var(--line)',
                borderRadius: 4,
                background: 'var(--paper)',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={submitNewGuest}
              disabled={!guestName.trim() || createGuest.isPending}
              style={{
                fontSize: 12,
                padding: '6px 10px',
                border: '1px solid var(--terra)',
                borderRadius: 4,
                background: 'var(--terra)',
                color: '#fff6e8',
                cursor: 'pointer',
              }}
            >
              {createGuest.isPending ? '…' : 'Add'}
            </button>
            <button
              type="button"
              onClick={cancelAddGuest}
              style={{
                fontSize: 12,
                padding: '6px 8px',
                border: '1px solid var(--line)',
                borderRadius: 4,
                background: 'transparent',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </ScorePadRow>
        ) : (
          <ScorePadRow
            onClick={() => setAddingGuest(true)}
            style={{ borderBottom: 'none', color: 'var(--ink-2)' }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: '1.5px dashed var(--line)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                color: 'var(--ink-3)',
                flexShrink: 0,
              }}
            >
              +
            </div>
            <div style={{ flex: 1, fontSize: 14, fontStyle: 'italic' }}>Add a guest</div>
          </ScorePadRow>
        )}
      </ScorePad>

      {createGuest.isError && (
        <div style={{ fontSize: 12, color: 'var(--alert, #b94a3a)' }}>
          Couldn’t add guest. Try a different name.
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>
        {participants.length === 0
          ? 'Pick at least 2 players.'
          : participants.length === 1
            ? 'One more.'
            : `${participants.length} picked. Drag to rerank in the next step.`}
      </div>

      <button type="button" className="btn-stamp" disabled={!canContinue} onClick={goResults}>
        Continue
      </button>
    </div>
  )
}
